import { useState, useEffect, useCallback } from 'react';
import axiosClient from './lib/axiosClient';
import BarcodeScanner from './BarcodeScanner';

const STATUS_COLORS = {
  available:   { bg: '#0f2a1a', text: '#4ade80', border: '#1a4a2a' },
  rented:      { bg: '#1a1a0f', text: '#facc15', border: '#3a3a10' },
  maintenance: { bg: '#1a1000', text: '#fb923c', border: '#3a2800' },
  repair:      { bg: '#2a0f0f', text: '#f87171', border: '#4a1a1a' },
  retired:     { bg: '#1a1a1a', text: '#6b7280', border: '#2a2a2a' },
};

const CONDITION_LABELS = { new: '★★★★★', excellent: '★★★★☆', good: '★★★☆☆', fair: '★★☆☆☆', poor: '★☆☆☆☆' };

const emptyVariant = {
  unit_status: 'available',
  condition: 'good',
  serial_number: '',
  sku: '',
  barcode: '',
  hour_meter: '',
  notes: '',
  property_values: {},
};

export default function VariantManager({ productGroupId, productName, propertySchema = [], productType = 'rental', onClose }) {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyVariant);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [scanField, setScanField] = useState(null); // 'serial_number' | 'barcode' | null

  const loadVariants = useCallback(() => {
    setLoading(true);
    axiosClient.get(`/api/rentals/products/${productGroupId}/variants`)
      .then(r => setVariants(r.data.variants || []))
      .catch(() => setError('Failed to load variants.'))
      .finally(() => setLoading(false));
  }, [productGroupId]);

  useEffect(() => { loadVariants(); }, [loadVariants]);

  const openAdd = () => { setForm({ ...emptyVariant, property_values: {} }); setEditingId('new'); setError(null); };
  const openEdit = (v) => {
    setForm({
      unit_status: v.unit_status || 'available',
      condition: v.condition || 'good',
      serial_number: v.serial_number || '',
      sku: v.sku || '',
      barcode: v.barcode || '',
      hour_meter: v.hour_meter ?? '',
      notes: v.notes || '',
      property_values: v.property_values || {},
    });
    setEditingId(v.id);
    setError(null);
  };
  const closeForm = () => { setEditingId(null); setError(null); };
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setProp = (key, val) => setForm(f => ({ ...f, property_values: { ...f.property_values, [key]: val } }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      hour_meter: form.hour_meter === '' ? null : parseFloat(form.hour_meter),
    };
    try {
      if (editingId === 'new') {
        await axiosClient.post(`/api/rentals/products/${productGroupId}/variants`, payload);
      } else {
        await axiosClient.patch(`/api/rentals/products/variants/${editingId}`, payload);
      }
      closeForm();
      loadVariants();
    } catch (e) {
      setError(e?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this unit? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await axiosClient.delete(`/api/rentals/products/variants/${id}`);
      loadVariants();
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 409) {
        if (body?.active_order_ids?.length) {
          setError(`Can't delete: unit has an active order (#${body.active_order_ids.join(', #')})`);
        } else if (body?.downtime_ids?.length) {
          setError(`Can't delete: unit has an open repair/downtime record`);
        } else {
          setError(body?.error || `Can't delete: unit is in use`);
        }
      } else {
        setError(body?.error || 'Delete failed.');
      }
    } finally {
      setDeleting(null);
    }
  };

  if (editingId !== null) {
    return (
      <div style={styles.wrap}>
        <div style={styles.header}>
          <div>
            <div style={styles.breadcrumb}>{productName}</div>
            <div style={styles.headerTitle}>{editingId === 'new' ? 'Add Unit' : 'Edit Unit'}</div>
          </div>
          <button onClick={closeForm} style={styles.closeBtn}>✕</button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {scanField && (
          <BarcodeScanner
            onRawScan={val => { set(scanField, val); setScanField(null); }}
            onClose={() => setScanField(null)}
          />
        )}

        <section style={styles.section}>
          <div style={styles.sectionLabel}>UNIT IDENTITY</div>
          <div style={styles.twoCol}>
            <div style={styles.row}>
              <label style={styles.label}>Serial Number</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...styles.input, flex: 1 }} value={form.serial_number} onChange={e => set('serial_number', e.target.value)} placeholder="SN-001" />
                <button type="button" onClick={() => setScanField('serial_number')} style={styles.scanBtn} title="Scan barcode into Serial Number">📷</button>
              </div>
            </div>
            <div style={styles.row}>
              <label style={styles.label}>SKU</label>
              <input style={styles.input} value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="GRD-001" />
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Barcode</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...styles.input, flex: 1 }} value={form.barcode} onChange={e => set('barcode', e.target.value)} placeholder="Scan or type barcode" />
                <button type="button" onClick={() => setScanField('barcode')} style={styles.scanBtn} title="Scan barcode into Barcode field">📷</button>
              </div>
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Status</label>
              <select style={styles.select} value={form.unit_status} onChange={e => set('unit_status', e.target.value)}>
                {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Condition</label>
              <select style={styles.select} value={form.condition} onChange={e => set('condition', e.target.value)}>
                {Object.keys(CONDITION_LABELS).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Hour Meter</label>
              <input style={styles.input} type="number" min="0" step="0.1" value={form.hour_meter} onChange={e => set('hour_meter', e.target.value)} placeholder="0" />
            </div>
          </div>
          <div style={styles.row}>
            <label style={styles.label}>Notes</label>
            <textarea style={{ ...styles.input, height: 56, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes about this unit" />
          </div>
        </section>

        {propertySchema.length > 0 && (
          <section style={styles.section}>
            <div style={styles.sectionLabel}>UNIT PROPERTIES</div>
            <div style={styles.twoCol}>
              {propertySchema.map(field => (
                <div key={field.key} style={styles.row}>
                  <label style={styles.label}>{field.label || field.key}</label>
                  {field.type === 'select' ? (
                    <select style={styles.select} value={form.property_values[field.key] || ''} onChange={e => setProp(field.key, e.target.value)}>
                      <option value="">—</option>
                      {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : field.type === 'boolean' ? (
                    <select style={styles.select} value={form.property_values[field.key] ?? ''} onChange={e => setProp(field.key, e.target.value === 'true')}>
                      <option value="">—</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input style={styles.input} type={field.type === 'number' ? 'number' : 'text'} value={form.property_values[field.key] || ''} onChange={e => setProp(field.key, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Per-unit price overrides removed - the columns still exist on
            rental_product_variants, but nothing on the pricing side ever
            read them (verified: zero references in public-rentals.js or
            rentals.js). Logged as a future feature, not re-added here. */}

        <div style={styles.actions}>
          <button onClick={closeForm} style={styles.secondaryBtn} disabled={saving}>Cancel</button>
          <button onClick={handleSave} style={styles.saveBtn} disabled={saving}>
            {saving ? 'Saving…' : editingId === 'new' ? 'Add Unit' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      {onClose && <button onClick={onClose} style={styles.backLink}>← Back to Inventory</button>}
      <div style={styles.header}>
        <div>
          <div style={styles.breadcrumb}>{productName}</div>
          <div style={styles.headerTitle}>Units <span style={{ color: '#666', fontSize: 14, fontWeight: 400 }}>({variants.length})</span></div>
        </div>
        <button onClick={openAdd} style={styles.saveBtn}>+ Add Unit</button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {loading && <div style={styles.loading}>Loading units…</div>}

      {!loading && variants.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
          <div style={{ color: '#888', fontSize: 14 }}>No units yet.</div>
          <button onClick={openAdd} style={{ ...styles.saveBtn, marginTop: 16 }}>+ Add First Unit</button>
        </div>
      )}

      {!loading && variants.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Serial / SKU</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Condition</th>
              <th style={styles.th}>Hours</th>
              {propertySchema.slice(0, 2).map(f => <th key={f.key} style={styles.th}>{f.label || f.key}</th>)}
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {variants.map(v => {
              const sc = STATUS_COLORS[v.unit_status] || STATUS_COLORS.available;
              return (
                <tr key={v.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600, color: '#e0e0f0' }}>{v.serial_number || '—'}</div>
                    {v.sku && <div style={{ fontSize: 11, color: '#666' }}>{v.sku}</div>}
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>
                      {v.unit_status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: '#f5c518', fontSize: 13 }}>{CONDITION_LABELS[v.condition] || v.condition}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ color: v.hour_meter > 500 ? '#fb923c' : '#aaa' }}>
                      {v.hour_meter != null ? `${v.hour_meter}h` : '—'}
                    </span>
                  </td>
                  {propertySchema.slice(0, 2).map(f => (
                    <td key={f.key} style={styles.td}>
                      <span style={{ color: '#bbb', fontSize: 13 }}>{v.property_values?.[f.key] ?? '—'}</span>
                    </td>
                  ))}
                  <td style={styles.td}>
                    <button onClick={() => openEdit(v)} style={styles.actionBtn}>Edit</button>
                    <button onClick={() => handleDelete(v.id)} style={styles.deleteBtn} disabled={deleting === v.id}>
                      {deleting === v.id ? '…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles = {
  wrap: { background: '#252840', border: '1px solid #2a2a4a', borderRadius: 10, padding: 24, color: '#e0e0f0', fontFamily: 'sans-serif' },
  backLink: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#a0a0ff', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '0 0 16px', marginBottom: 4 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  breadcrumb: { fontSize: 12, color: '#5a5a8a', marginBottom: 2 },
  headerTitle: { fontSize: 18, fontWeight: 700, color: '#fff' },
  closeBtn: { background: 'none', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer' },
  section: { marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #2a2a4a' },
  sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: '#6c6c9a', marginBottom: 12 },
  row: { marginBottom: 12 },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  label: { display: 'block', fontSize: 12, color: '#aaa', marginBottom: 4 },
  input: { width: '100%', background: '#0f0f20', border: '1px solid #3a3a5a', borderRadius: 6, padding: '8px 10px', color: '#e0e0f0', fontSize: 14, boxSizing: 'border-box' },
  scanBtn: { flexShrink: 0, padding: '0 12px', borderRadius: 6, border: '1px solid #2a2a5a', background: '#1a1a3a', color: '#a0a0ff', cursor: 'pointer', fontSize: 15 },
  select: { width: '100%', background: '#0f0f20', border: '1px solid #3a3a5a', borderRadius: 6, padding: '8px 10px', color: '#e0e0f0', fontSize: 14 },
  inputPrefix: { position: 'relative' },
  prefix: { position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#6c6c9a', fontSize: 14 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 },
  saveBtn: { background: '#4a4aff', border: 'none', color: '#fff', borderRadius: 6, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { background: '#2a2a4a', border: 'none', color: '#aaa', borderRadius: 6, padding: '9px 20px', fontSize: 13, cursor: 'pointer' },
  error: { background: '#3a1a1a', border: '1px solid #a03030', color: '#ff8080', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 13 },
  loading: { color: '#666', padding: '20px 0', textAlign: 'center' },
  emptyState: { textAlign: 'center', padding: '40px 0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#6c6c9a', borderBottom: '1px solid #2a2a4a' },
  tr: { borderBottom: '1px solid #1e1e36' },
  td: { padding: '10px 12px', fontSize: 13, verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  actionBtn: { background: '#2a2a5a', border: 'none', color: '#a0a0ff', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6 },
  deleteBtn: { background: 'none', border: '1px solid #4a1a1a', color: '#f87171', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
};
